// client/src/components/clientes/Clientes.js - VERSÃO 100% CORRIGIDA
import React, { Fragment, useState, useCallback, useMemo } from 'react';
import { useClientes, useMutateClientes } from '../../hooks/useClientes';
import ClienteForm from './ClienteForm';
import ClienteRow from './ClienteRow';
import FiltroClientes from './FiltroClientes';
import {
    PlusCircle,
    Filter as FilterIcon,
    Loader2,
    Users,
    XCircle,
    AlertTriangle,
    RefreshCw
} from 'lucide-react';
import GaleriaAntesDepois from './uploads/GaleriaAntesDepois';
import { useMe } from '../../hooks/useAuth';
import PageHeader from '../layout/PageHeader';
import Pagination from '../common/Pagination';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';
import { Eye, Trash2, Image as ImageIcon } from 'lucide-react';

const Clientes = () => {
    // ✅ ESTADOS SIMPLIFICADOS
    const [viewState, setViewState] = useState({
        showList: true,
        showForm: false,
        showGaleria: false,
    });

    const [filtros, setFiltros] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [currentCliente, setCurrentCliente] = useState(null);
    const [selectedCliente, setSelectedCliente] = useState(null);

    const clientesPerPage = 20;
    const { accountId, isLoading: userLoading } = useMe();

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: clientesPerPage,
        ...filtros
    }), [currentPage, filtros]);

    // ✅ HOOK COM FILTROS
    const { 
        data: clientesData, 
        isLoading, 
        refetch, 
        error,
        isFetching 
    } = useClientes(queryParams, accountId);
    
    const { deleteCliente } = useMutateClientes();
    const isDeleting = deleteCliente.isPending;

    // ✅ CORREÇÃO CRÍTICA: Verificar se clientesData existe antes de acessar propriedades
    const clientes = clientesData?.data || [];
    const totalClientes = clientesData?.count || 0;
    const totalCount = clientesData?.totalCount || 0;
    const pagination = clientesData?.pagination || {};
    const totalPages = pagination.totalPages || Math.ceil(totalCount / clientesPerPage) || 1;
    
    const handleApplyFilters = useCallback((novosFiltros) => {
        setCurrentPage(1);
        setFiltros(novosFiltros);
    }, []);

    const handleClearFilters = useCallback(() => {
        setFiltros({});
        setCurrentPage(1);
    }, []);

    const handleAdicionarClick = useCallback(() => { 
        setCurrentCliente(null); 
        setViewState({ showList: false, showForm: true, showGaleria: false });
        setShowFilters(false);
    }, []);

    const handleCloseForm = useCallback(() => {
        setViewState({ showList: true, showForm: false, showGaleria: false });
        setCurrentCliente(null);
    }, []);

    const handleDelete = useCallback((clienteId) => {
        if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
            deleteCliente.mutate(clienteId, {
                onSuccess: () => setSelectedCliente(null),
            });
        }
    }, [deleteCliente]);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForm();
    }, [handleCloseForm]);

    const handleEditClick = useCallback((cliente) => {
        setCurrentCliente(cliente);
        setViewState({ showList: false, showForm: true, showGaleria: false });
        setShowFilters(false);
        setSelectedCliente(null);
    }, []);

    const handleGaleriaClick = useCallback((cliente) => {
        setCurrentCliente(cliente);
        setViewState({ showList: false, showForm: false, showGaleria: true });
        setShowFilters(false);
        setSelectedCliente(null);
    }, []);

    const handleRetryLoad = useCallback(() => {
        refetch();
    }, [refetch]);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (userLoading) {
        return (
            <div className="page-management-container">
                <div className="page-header">
                    <h1 className="page-title"><Users className="icon-title"/>Gerenciamento de Clientes</h1>
                </div>
                <div className="table-loading-state">
                    <Loader2 size={24} className="animate-spin" />
                    <span>Carregando informações do usuário...</span>
                </div>
            </div>
        );
    }

    const hasError = !!error;
    const hasFilters = Object.keys(filtros).length > 0;
    const isFilterPanelOpen = showFilters && viewState.showList;

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Users />}
                    title="Gerenciamento de Clientes"
                    actions={<>
                        {!viewState.showForm && !viewState.showGaleria && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    title={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {(viewState.showForm || viewState.showGaleria) && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {/* Formulário */}
                {viewState.showForm && (
                    <div className="content-panel">
                        <ClienteForm 
                            key={currentCliente?._id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            current={currentCliente}
                        />
                    </div>
                )}

                {/* Galeria */}
                {viewState.showGaleria && currentCliente && (
                    <div className="content-panel">
                        <div className="form-header">
                            <h3 className="form-title">
                                Galeria do Cliente: {currentCliente.user?.name || 'Cliente'}
                            </h3>
                            <button type="button" onClick={handleCloseForm} title="Voltar" className="form-close-button">
                                <XCircle size={24} />
                            </button>
                        </div>
                        <GaleriaAntesDepois clienteId={currentCliente._id} />
                    </div>
                )}

                {/* Filtros */}
                {isFilterPanelOpen && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtrar Clientes</h3>
                        <FiltroClientes
                            onApplyFilters={handleApplyFilters}
                            loading={isLoading || isFetching}
                        />
                    </div>
                )}
            </div>

            {/* Lista de Clientes */}
            {viewState.showList && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {/* Estado de Erro */}
                        {hasError ? (
                            <div className="table-error-state">
                                <div className="flex items-center justify-center mb-4">
                                    <AlertTriangle size={48} className="text-red-500" />
                                </div>
                                <div className="text-red-600 font-medium text-lg mb-2 text-center">
                                    ❌ Erro ao carregar clientes
                                </div>
                                <p className="text-gray-600 text-sm mb-4 text-center max-w-md mx-auto">
                                    {error.message || 'Ocorreu um erro ao buscar os clientes.'}
                                </p>
                                <div className="flex flex-col sm:flex-row justify-center gap-3">
                                    <button
                                        onClick={handleRetryLoad}
                                        className="action-button action-button-primary"
                                    >
                                        <RefreshCw size={18} className="icon" />
                                        Tentar novamente
                                    </button>
                                </div>
                            </div>
                        ) : 
                        
                        /* Estado de Carregamento */
                        (isLoading || isFetching) && clientes.length === 0 ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando clientes...</span>
                                {hasFilters && (
                                    <span className="text-xs text-blue-500 mt-1">
                                        Aplicando filtros...
                                    </span>
                                )}
                            </div>
                        ) : 
                        
                        /* Lista com Clientes */
                        clientes.length > 0 ? (
                            <React.Fragment>
                                <table className="styled-table">
                                    <thead>
                                        <tr>
                                            <th style={{textAlign:'left'}}>Cliente</th>
                                            <th>Telefone</th>
                                            <th style={{textAlign:'right'}}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientes.map(cliente => (
                                            <ClienteRow
                                                key={cliente._id}
                                                cliente={cliente}
                                                onOpenActions={setSelectedCliente}
                                                loading={isDeleting}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                                <Pagination
                                    totalItems={totalCount}
                                    itemsPerPage={clientesPerPage}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                />
                            </React.Fragment>
                        ) : 
                        
                        /* Estado Vazio */
                        (
                            <div className="table-empty-state">
                                <div className="mb-4">
                                    <Users size={48} className="text-gray-400 mx-auto" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                                    {hasFilters 
                                        ? 'Nenhum cliente encontrado' 
                                        : 'Nenhum cliente cadastrado'}
                                </h3>
                                <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                                    {hasFilters 
                                        ? 'Tente ajustar os filtros de busca.'
                                        : accountId 
                                            ? 'Clique em "Adicionar Cliente" para adicionar seu primeiro cliente.'
                                            : 'Aguardando carregamento da conta...'}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    {hasFilters ? (
                                        <>
                                            <button 
                                                onClick={handleClearFilters}
                                                className="action-button action-button-primary"
                                            >
                                                Limpar Filtros
                                            </button>
                                            <button
                                                onClick={handleAdicionarClick}
                                                className="action-button action-button-secondary"
                                            >
                                                <PlusCircle size={18} className="icon" />
                                                Criar Adicionar Cliente
                                            </button>
                                        </>
                                    ) : accountId && (
                                        <button
                                            onClick={handleAdicionarClick}
                                            className="action-button action-button-primary"
                                        >
                                            <PlusCircle size={18} className="icon" />
                                            Adicionar Primeiro Cliente
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FabButton
                onClick={handleAdicionarClick}
                title="Adicionar Cliente"
                visible={viewState.showList && !viewState.showForm && !viewState.showGaleria}
                disabled={!accountId}
                icon={<Users size={26} className="icon"/>}
            />

            <ActionsDrawer
                open={!!selectedCliente}
                onClose={() => setSelectedCliente(null)}
                title={selectedCliente?.user?.name || 'Cliente sem nome'}
                subtitle={selectedCliente?.user?.telefone}
                actions={selectedCliente ? [
                    {
                        key: 'editar',
                        label: 'Visualizar/Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedCliente),
                    },
                    {
                        key: 'galeria',
                        label: 'Galeria de Imagens',
                        icon: <ImageIcon size={20} className="icon" />,
                        onClick: () => handleGaleriaClick(selectedCliente),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedCliente._id),
                        destructive: true,
                        loading: isDeleting,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default Clientes;